import { loadData, saveData } from "./data.js";
import { appConfig } from "./config.js";
import { canAddAthlete, hasFeature, resolveEntitlements } from "./subscription.js";
import { captureError, enableMonitoring } from "./monitoring.js";

const app = document.querySelector("#app");
const toast = document.querySelector("#toast");
const appVersion = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";
let data = loadData();
let route = "dashboard";
let athleteFilter = "Tümü";
let searchTerm = "";
let currentProfile = null;
let currentAuthUser = null;
let sessionStatus = appConfig.demoMode ? "ready" : "loading";
let workspaceLoadError = null;
let authApi = null;
let repositoryApi = null;
let entitlements = resolveEntitlements(
  appConfig.demoMode ? { status: "active", planId: "club" } : null
);

const icons = {
  dashboard: "⌁",
  athletes: "♟",
  races: "⚑",
  progress: "↗",
  family: "✉",
  applications: "◎",
  account: "⚙"
};

const allRoutes = [
  ["dashboard", "Genel Bakış"],
  ["athletes", "Sporcular"],
  ["races", "Yarış Takvimi"],
  ["progress", "Gelişim"],
  ["family", "Aile Özeti"],
  ["applications", "Üyelik Başvuruları"],
  ["account", "Hesabım"]
];

const allowedRoutes = {
  coach: ["dashboard", "athletes", "races", "progress", "family", "account"],
  admin: ["dashboard", "athletes", "races", "progress", "family", "applications", "account"],
  athlete: ["dashboard", "races", "progress", "account"],
  parent: ["dashboard", "races", "family", "account"]
};

function visibleRoutes() {
  return allRoutes.filter(([key]) => allowedRoutes[data.currentRole].includes(key));
}

function daysUntil(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((new Date(`${date}T00:00:00`) - today) / 86400000));
}

function age(birthDate) {
  const today = new Date();
  const birth = new Date(`${birthDate}T00:00:00`);
  let years = today.getFullYear() - birth.getFullYear();
  const birthdayPassed =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!birthdayPassed) years -= 1;
  return years;
}

function initials(athlete) {
  return `${athlete.firstName[0]}${athlete.lastName[0]}`;
}

function nextRaceFor(athleteId) {
  return data.races
    .filter(r => r.assignedAthleteIds.includes(athleteId) && r.status === "planned")
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
}

function showToast(message) {
  toast.textContent = message;
  toast.classList.add("show");
  setTimeout(() => toast.classList.remove("show"), 2400);
}

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function layout(content, title, eyebrow = "SAILFORCE • COACH CENTER") {
  const roleLabels = { coach: "Antrenör", athlete: "Sporcu", parent: "Aile / Veli", admin: "Kulüp Admini" };
  const displayName = currentProfile?.fullName || (
    data.currentRole === "coach" ? "Deniz Arman" :
    data.currentRole === "parent" ? "Selin Kaya" :
    data.currentRole === "athlete" ? "Ege Kaya" : "Kulüp Admini"
  );
  const avatarText = displayName.split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();
  app.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand">
          <img class="brand-mark" src="/assets/sailforce-logo.png" alt="SailForce amblemi" />
          <div class="brand-name">Sail<span>Force</span></div>
        </div>
        <div class="nav-label">YÖNETİM</div>
        <nav class="nav">
          ${visibleRoutes().map(([key, label]) => `<button class="nav-button ${route === key ? "active" : ""}" data-route="${key}"><span class="nav-icon">${icons[key]}</span>${label}</button>`).join("")}
        </nav>
        <div class="sidebar-footer">
          <div class="club-card"><small>KULÜP</small><strong>İstanbul Yelken Akademi</strong></div>
          <div class="plan-card"><small>PLAN</small><strong>${entitlements.name}</strong><span>${data.athletes.length}/${entitlements.maxAthletes} sporcu</span></div>
          <div class="legal-links"><a href="/kvkk.html">KVKK</a><a href="/privacy.html">Gizlilik</a></div>
        </div>
      </aside>
      <main class="main">
        <header class="topbar">
          <div><p class="eyebrow">${eyebrow}</p><h1 class="page-title">${title}</h1></div>
          <div class="top-actions">
            ${appConfig.demoMode ? `
              <select class="role-select" id="roleSelect" aria-label="Rol görünümü">
                ${Object.entries(roleLabels).map(([key, label]) => `<option value="${key}" ${data.currentRole === key ? "selected" : ""}>${label}</option>`).join("")}
              </select>` : `<span class="role-badge">${roleLabels[data.currentRole] || "Beklemede"}</span>`}
            <button class="avatar avatar-button" id="logoutButton" title="Çıkış yap">${avatarText}</button>
          </div>
        </header>
        <div class="content">${content}</div>
      </main>
      <nav class="mobile-nav">
        ${visibleRoutes().map(([key, label]) => `<button class="${route === key ? "active" : ""}" data-route="${key}"><span>${icons[key]}</span>${label.split(" ")[0]}</button>`).join("")}
      </nav>
    </div>`;
  bindShell();
}

function bindShell() {
  document.querySelectorAll("[data-route]").forEach(btn => btn.addEventListener("click", () => {
    route = btn.dataset.route;
    render();
  }));
  document.querySelector("#roleSelect")?.addEventListener("change", e => {
    data.currentRole = e.target.value;
    saveData(data);
    route = "dashboard";
    render();
    showToast(`${e.target.options[e.target.selectedIndex].text} görünümüne geçildi`);
  });
  document.querySelector("#logoutButton").addEventListener("click", async () => {
    if (appConfig.demoMode) {
      data.loggedIn = false;
      saveData(data);
      render();
      return;
    }
    await authApi.logout();
  });
}

function demoLoginPage() {
  app.innerHTML = `<main class="login-page">
    <section class="login-brand">
      <div class="login-logo"><img src="/assets/sailforce-logo.png" alt="SailForce" /></div>
      <p class="eyebrow">YELKEN SPORCU YÖNETİMİ</p>
      <h1>Her sporcunun rotası<br><span>tek merkezde.</span></h1>
      <p>Gelişim, yarış hazırlığı ve aile iletişimi için sade, güvenilir çalışma alanı.</p>
      <div class="login-feature-row"><span>✓ Sporcu gelişimi</span><span>✓ Yarış takvimi</span><span>✓ Veli özeti</span></div>
    </section>
    <section class="login-panel">
      <div class="login-card">
        <p class="eyebrow">DEMO GİRİŞ</p><h2>Rolünüzü seçin</h2><p>Her rol yalnızca yetkili olduğu alanları görür.</p>
        <div class="role-cards">
          ${[
            ["coach","⛵","Antrenör","Takım, yarış ve gelişim yönetimi"],
            ["athlete","↗","Sporcu","Kendi programı ve hedefleri"],
            ["parent","♡","Aile / Veli","Çocuğunuzun sade gelişim özeti"],
            ["admin","⌘","Kulüp Admini","Kulüp genel görünümü"]
          ].map(([key, icon, name, text]) => `<button class="role-card" data-login-role="${key}"><span>${icon}</span><div><strong>${name}</strong><small>${text}</small></div><b>→</b></button>`).join("")}
        </div>
        <small class="privacy-note">🔒 Demo verileri yalnızca bu tarayıcıda saklanır.</small>
        <div class="login-legal"><a href="/kvkk.html">KVKK Aydınlatma</a><a href="/privacy.html">Gizlilik</a><a href="/terms.html">Kullanım Koşulları</a></div>
      </div>
    </section>
  </main>`;
  document.querySelectorAll("[data-login-role]").forEach(button => button.addEventListener("click", () => {
    data.currentRole = button.dataset.loginRole;
    data.loggedIn = true;
    route = "dashboard";
    saveData(data);
    render();
    showToast("SailForce'a hoş geldiniz");
  }));
}

function productionLoginPage(mode = "login") {
  const isRegister = mode === "register";
  app.innerHTML = `<main class="login-page">
    <section class="login-brand">
      <div class="login-logo"><img src="/assets/sailforce-logo.png" alt="SailForce" /></div>
      <p class="eyebrow">YELKEN SPORCU YÖNETİMİ</p>
      <h1>Her sporcunun rotası<br><span>tek merkezde.</span></h1>
      <p>Gelişim, yarış hazırlığı ve aile iletişimi için sade, güvenilir çalışma alanı.</p>
      <div class="login-feature-row"><span>✓ Sporcu gelişimi</span><span>✓ Yarış takvimi</span><span>✓ Veli özeti</span></div>
    </section>
    <section class="login-panel">
      <div class="login-card">
        <div class="auth-tabs">
          <button type="button" class="${isRegister ? "" : "active"}" data-auth-mode="login">Giriş yap</button>
          <button type="button" class="${isRegister ? "active" : ""}" data-auth-mode="register">Üye ol</button>
        </div>
        <p class="eyebrow">${isRegister ? "GÜVENLİ ÜYELİK" : "GÜVENLİ GİRİŞ"}</p>
        <h2>${isRegister ? "SailForce hesabınızı oluşturun" : "SailForce'a giriş yapın"}</h2>
        <p>${isRegister ? "Başvurunuz e-posta doğrulaması ve kulüp onayından sonra etkinleşir." : "Kulübünüz tarafından tanımlanan hesabınızı kullanın."}</p>
        <form id="loginForm" class="login-form">
          ${isRegister ? `
          <div class="field">
            <label>Ad soyad</label>
            <input name="fullName" autocomplete="name" minlength="3" required />
          </div>
          <div class="field">
            <label>Kullanım amacı</label>
            <select name="requestedRole" required>
              <option value="coach">Antrenör</option>
              <option value="athlete">Sporcu</option>
              <option value="parent">Aile / Veli</option>
            </select>
            <small class="field-help">Bu seçim yetki vermez; kulüp yöneticisi tarafından doğrulanır.</small>
          </div>
          <div class="field">
            <label>Kulüp davet kodu</label>
            <input name="clubJoinCode" autocomplete="off" minlength="6" maxlength="12" required />
            <small class="field-help">Bu kodu kulüp yöneticinizden alabilirsiniz.</small>
          </div>` : ""}
          <div class="field">
            <label>E-posta</label>
            <input name="email" type="email" autocomplete="email" required />
          </div>
          <div class="field">
            <label>Şifre</label>
            <input name="password" type="password" autocomplete="${isRegister ? "new-password" : "current-password"}" minlength="8" required />
          </div>
          ${isRegister ? `
          <div class="consent-list">
            <label><input name="acceptsKvkk" type="checkbox" required /> <span><a href="/kvkk.html" target="_blank">KVKK Aydınlatma Metni</a>'ni okudum.</span></label>
            <label><input name="acceptsPrivacy" type="checkbox" required /> <span><a href="/privacy.html" target="_blank">Gizlilik Politikası</a>'nı okudum ve kabul ediyorum.</span></label>
            <label><input name="acceptsTerms" type="checkbox" required /> <span><a href="/terms.html" target="_blank">Kullanım Koşulları</a>'nı kabul ediyorum.</span></label>
            <label><input name="acceptsExplicitConsent" type="checkbox" required /> <span>Sporcu gelişimi için gerekli kişisel ve sınırlı fiziksel verilerin işlenmesine açık rıza veriyorum.</span></label>
            <label class="optional-consent"><input name="acceptsEmailCommunication" type="checkbox" /> <span>Ürün duyurularını e-posta ile almak istiyorum. (İsteğe bağlı)</span></label>
          </div>` : ""}
          <div class="login-error" id="loginError" role="alert"></div>
          <button class="primary-button login-submit" type="submit">${isRegister ? "Başvuruyu oluştur" : "Giriş yap"}</button>
          ${isRegister ? "" : `<button class="text-button" id="resetPassword" type="button">Şifremi unuttum</button>`}
        </form>
        <small class="privacy-note">🔒 Rolünüz ve erişiminiz kulüp yöneticiniz tarafından belirlenir.</small>
        <div class="login-legal"><a href="/kvkk.html">KVKK Aydınlatma</a><a href="/privacy.html">Gizlilik</a><a href="/terms.html">Kullanım Koşulları</a></div>
      </div>
    </section>
  </main>`;

  const form = document.querySelector("#loginForm");
  const errorBox = document.querySelector("#loginError");
  const submitButton = form.querySelector("[type=submit]");

  document.querySelectorAll("[data-auth-mode]").forEach(button => {
    button.addEventListener("click", () => productionLoginPage(button.dataset.authMode));
  });

  form.addEventListener("submit", async event => {
    event.preventDefault();
    errorBox.textContent = "";
    submitButton.disabled = true;
    submitButton.textContent = isRegister ? "Hesap oluşturuluyor…" : "Giriş yapılıyor…";
    const values = Object.fromEntries(new FormData(form));
    try {
      if (isRegister) {
        await authApi.registerAccount({
          ...values,
          acceptsKvkk: values.acceptsKvkk === "on",
          acceptsPrivacy: values.acceptsPrivacy === "on",
          acceptsTerms: values.acceptsTerms === "on",
          acceptsExplicitConsent: values.acceptsExplicitConsent === "on",
          acceptsEmailCommunication: values.acceptsEmailCommunication === "on"
        });
      } else {
        await authApi.loginWithEmail(values.email, values.password);
      }
    } catch (error) {
      errorBox.textContent = authApi.authErrorMessage(error);
      submitButton.disabled = false;
      submitButton.textContent = isRegister ? "Başvuruyu oluştur" : "Giriş yap";
    }
  });

  document.querySelector("#resetPassword")?.addEventListener("click", async () => {
    const email = form.elements.email.value.trim();
    if (!email) {
      errorBox.textContent = "Önce e-posta adresinizi girin.";
      return;
    }
    try {
      await authApi.requestPasswordReset(email);
      errorBox.classList.add("success");
      errorBox.textContent = "Şifre yenileme bağlantısı e-posta adresinize gönderildi.";
    } catch (error) {
      errorBox.classList.remove("success");
      errorBox.textContent = authApi.authErrorMessage(error);
    }
  });
}

function emailVerificationPage() {
  app.innerHTML = `<main class="state-page"><div class="state-card">
    <div class="state-icon">✉</div>
    <p class="eyebrow">E-POSTA DOĞRULAMA</p>
    <h1>E-posta adresinizi doğrulayın</h1>
    <p>${currentAuthUser?.email || "E-posta adresinize"} gönderdiğimiz bağlantıya tıklayın. Doğruladıktan sonra aşağıdaki düğmeyle devam edin.</p>
    <div class="state-actions">
      <button class="primary-button" id="verificationCheck">Doğruladım, kontrol et</button>
      <button class="secondary-button" id="verificationResend">Bağlantıyı yeniden gönder</button>
      <button class="text-button" id="verificationLogout">Çıkış yap</button>
    </div>
    <div class="login-error" id="verificationError" role="alert"></div>
  </div></main>`;

  const errorBox = document.querySelector("#verificationError");
  document.querySelector("#verificationCheck").addEventListener("click", async () => {
    const user = await authApi.refreshCurrentUser();
    if (user?.emailVerified) {
      window.location.reload();
      return;
    }
    errorBox.textContent = "E-posta henüz doğrulanmamış görünüyor.";
  });
  document.querySelector("#verificationResend").addEventListener("click", async () => {
    try {
      await authApi.resendVerificationEmail();
      errorBox.classList.add("success");
      errorBox.textContent = "Doğrulama bağlantısı yeniden gönderildi.";
    } catch (error) {
      errorBox.classList.remove("success");
      errorBox.textContent = authApi.authErrorMessage(error);
    }
  });
  document.querySelector("#verificationLogout").addEventListener("click", authApi.logout);
}

function loadingPage() {
  app.innerHTML = `<main class="state-page"><div class="state-card"><div class="state-spinner"></div><h1>SailForce hazırlanıyor</h1><p>Güvenli oturumunuz kontrol ediliyor.</p></div></main>`;
}

function pendingAccountPage() {
  app.innerHTML = `<main class="state-page"><div class="state-card">
    <div class="state-icon">⏳</div>
    <p class="eyebrow">HESAP ONAYI</p>
    <h1>Hesabınız hazırlanıyor</h1>
    <p>${currentProfile?.email || "Hesabınız"} için kulüp ve rol ataması henüz tamamlanmadı. Kulüp yöneticiniz hesabı etkinleştirdiğinde giriş yapabilirsiniz.</p>
    <button class="secondary-button" id="pendingLogout">Çıkış yap</button>
  </div></main>`;
  document.querySelector("#pendingLogout").addEventListener("click", authApi.logout);
}

function accessDeniedPage() {
  app.innerHTML = `<main class="state-page"><div class="state-card">
    <div class="state-icon">!</div>
    <p class="eyebrow">ERİŞİM KAPALI</p>
    <h1>Hesabınız etkin değil</h1>
    <p>Bu hesap devre dışı bırakılmış veya kulüp erişimi kaldırılmış. Kulüp yöneticinizle iletişime geçin.</p>
    <button class="secondary-button" id="deniedLogout">Çıkış yap</button>
  </div></main>`;
  document.querySelector("#deniedLogout").addEventListener("click", authApi.logout);
}

function workspaceErrorReference() {
  const code = String(workspaceLoadError?.code || "unknown")
    .replace(/[^a-z0-9/-]/gi, "-")
    .replace(/-+/g, "-")
    .slice(0, 40)
    .toUpperCase();
  return `SF-WORKSPACE-${code || "UNKNOWN"}`;
}

function workspaceErrorPage() {
  const reference = workspaceErrorReference();
  const supportLink = `mailto:${appConfig.supportEmail}?subject=${encodeURIComponent(`SailForce giriş desteği ${reference}`)}`;
  app.innerHTML = `<main class="state-page"><div class="state-card">
    <div class="state-icon">↻</div>
    <p class="eyebrow">GEÇİCİ BAĞLANTI SORUNU</p>
    <h1>Çalışma alanı açılamadı</h1>
    <p>Hesabınız etkin. Verileriniz yüklenirken geçici bir sorun oluştu. Birkaç saniye sonra yeniden deneyebilirsiniz.</p>
    <p class="support-copy">Sorun devam ederse destek kodunu ve sürümü iletin: <strong>${reference}</strong> · v${appVersion}</p>
    <div class="state-actions">
      <button class="primary-button" id="workspaceRetry">Yeniden dene</button>
      <a class="secondary-button support-button" href="${supportLink}">Destek al</a>
      <button class="text-button" id="workspaceLogout">Çıkış yap</button>
    </div>
    <div class="login-error" role="alert">${workspaceLoadError?.code === "permission-denied" ? "Oturum yetkileri yenileniyor. Çıkış yapıp tekrar giriş yapmak da sorunu çözebilir." : ""}</div>
  </div></main>`;
  document.querySelector("#workspaceRetry").addEventListener("click", loadCurrentWorkspace);
  document.querySelector("#workspaceLogout").addEventListener("click", authApi.logout);
}

function coachDashboard() {
  if (!data.athletes.length && !data.races.length) {
    return onboardingEmptyState("Kulübünüz hazır", "İlk sporcunuzu ve hedef yarışınızı ekleyerek SailForce'u kullanmaya başlayın.", [
      ["athletes", "Sporcu ekle"],
      ["races", "Yarış ekle"]
    ]);
  }
  const nearest = [...data.races].sort((a,b) => new Date(a.startDate) - new Date(b.startDate))[0];
  const avgAttendance = data.athletes.length
    ? Math.round(data.athletes.reduce((sum, a) => sum + Number(a.attendance || 0), 0) / data.athletes.length)
    : 0;
  const fitnessMinutes = data.workoutLogs.reduce((sum, w) => sum + w.durationMinutes, 0);
  return `
    <section class="grid stats-grid">
      ${statCard("Aktif sporcu", data.athletes.filter(a => a.status === "active").length, "♟", "+2 bu ay", "")}
      ${statCard("Bugün sahada", 14, "✓", "%82 katılım", "sea")}
      ${statCard("Fitness süresi", `${fitnessMinutes + 387} dk`, "⌁", "+%12", "green")}
      ${statCard("Ortalama katılım", `%${avgAttendance}`, "◔", "Hedef %85", "warn")}
    </section>
    ${nearest ? `<div class="section-head"><div><h2>Yarış hazırlığı</h2><p>En yakın hedef ve takımın hazırlık görünümü</p></div></div>
    <section class="grid two-col">
      <article class="card hero-race">
        <span class="tag">SIRADAKİ HEDEF • ${nearest.targetLevel.toUpperCase()}</span>
        <h2>${nearest.title}</h2>
        <p>⚑ ${nearest.location} · ${nearest.sailingClass}</p>
        <div class="countdown"><strong>${daysUntil(nearest.startDate)}</strong><span>gün kaldı</span></div>
        <div class="race-meta">
          <div><small>TAKIM</small><strong>${nearest.assignedAthleteIds.length} sporcu hazır</strong></div>
          <div><small>HEDEF</small><strong>${nearest.goal}</strong></div>
        </div>
      </article>
      <article class="card panel">
        <div class="panel-title"><h3>Hazırlık durumu</h3><button class="link-button" data-route="progress">Detaylar</button></div>
        <div class="progress-list">
          ${progressRow("Teknik hazırlık", 84)}
          ${progressRow("Fiziksel hazırlık", 76)}
          ${progressRow("Katılım disiplini", 91)}
          ${progressRow("Yarış hedefleri", 68)}
        </div>
      </article>
    </section>` : `<section class="card empty-action"><h2>Henüz hedef yarış yok</h2><p>Takım hazırlığını takip etmek için ilk yarışı ekleyin.</p><button class="primary-button" data-route="races">Yarış takvimine git</button></section>`}
    <div class="section-head"><div><h2>Takımın nabzı</h2><p>Öncelik verilmesi gereken gelişmeler</p></div></div>
    <section class="grid two-col">
      <article class="card panel">
        <div class="panel-title"><h3>Sporcu gelişimi</h3><button class="link-button" data-route="athletes">Tüm sporcular</button></div>
        <div class="progress-list">
          ${data.athletes.slice(0,4).map(a => progressRow(`${a.firstName} ${a.lastName}`, a.score, `${a.sailingClass} · %${a.attendance} katılım`)).join("")}
        </div>
      </article>
      <article class="card panel">
        <div class="panel-title"><h3>Son hareketler</h3><span class="race-state">Canlı</span></div>
        <div class="activity-list">
          ${activity("✓", "Yoklama tamamlandı", "Optimist grubu · 14 sporcu", "12 dk")}
          ${activity("⚖", "Yeni ölçüm eklendi", "Ege Kaya · 43,2 kg", "2 sa")}
          ${activity("✉", "Veli özeti hazır", "3 taslak gönderilmeyi bekliyor", "Bugün")}
          ${activity("⚑", "Yarış sonucu girildi", "Bahar Kupası · Ege 8.", "Dün")}
        </div>
      </article>
    </section>`;
}

function statCard(label, value, icon, trend, type) {
  return `<article class="card stat-card ${type}"><div class="stat-top"><span class="stat-icon">${icon}</span><span class="trend">${trend}</span></div><div class="stat-value">${value}</div><div class="stat-label">${label}</div></article>`;
}

function progressRow(label, value, detail = "") {
  return `<div class="progress-row"><div><strong>${label}</strong>${detail ? `<br><span>${detail}</span>` : ""}</div><strong>%${value}</strong><div class="progress-track"><div class="progress-fill" style="width:${value}%"></div></div></div>`;
}

function activity(icon, title, detail, time) {
  return `<div class="activity"><div class="activity-icon">${icon}</div><div><p>${title}</p><small>${detail}</small></div><time>${time}</time></div>`;
}

function athleteDashboard() {
  const athlete = data.athletes[0];
  if (!athlete) return onboardingEmptyState("Profiliniz hazırlanıyor", "Sporcu profiliniz kulüp yöneticiniz tarafından hesabınıza bağlanacak.", []);
  const race = nextRaceFor(athlete.id);
  return `
    <section class="card family-hero"><p class="eyebrow">MERHABA EGE 👋</p><h2>Rotan net, tempon güçlü.</h2><p>Bu hafta 3 antrenmanın 3'ünü tamamladın. Yarış öncesi odağın: start ve rüzgâr üstü hız.</p></section>
    <div class="section-head"><div><h2>Benim durumum</h2><p>Güncel hazırlık ve gelişim görünümün</p></div></div>
    <section class="grid stats-grid">
      ${statCard("Katılım", `%${athlete.attendance}`, "✓", "Çok iyi", "green")}
      ${statCard("Fitness", `%${athlete.fitness}`, "⌁", "+%8", "sea")}
      ${statCard("Gelişim skoru", athlete.score, "↗", "+4 puan", "")}
      ${statCard("Son yarış", "8.", "⚑", "64 sporcu", "warn")}
    </section>
    ${race ? `<section class="card hero-race" style="margin-top:16px">
      <span class="tag">SIRADAKİ YARIŞIN</span><h2>${race.title}</h2><p>⚑ ${race.location}</p>
      <div class="countdown"><strong>${daysUntil(race.startDate)}</strong><span>gün kaldı</span></div>
    </section>` : `<section class="card empty-action" style="margin-top:16px"><h2>Henüz yarış atanmamış</h2><p>Koçunuz yarış planını oluşturduğunda burada göreceksiniz.</p></section>`}
    <section class="grid two-col" style="margin-top:16px">
      <article class="card panel"><div class="panel-title"><h3>Koçunun notu</h3><span class="race-state">Yeni</span></div><p style="color:var(--muted);line-height:1.65">Start çizgisindeki kararların belirgin biçimde iyileşti. Bu hafta dönüşlerde tekne hızını korumaya odaklanıyoruz. İyi iş, ritmi koru! ⛵</p></article>
      <article class="card panel"><div class="panel-title"><h3>Haftalık hedef</h3></div>${progressRow("3 / 4 hedef tamamlandı", 75)}</article>
    </section>`;
}

function parentDashboard() {
  const athlete = data.athletes[0];
  if (!athlete) return onboardingEmptyState("Sporcu bağlantısı bekleniyor", "Çocuğunuzun profili hesabınıza bağlandığında gelişim bilgileri burada görünecek.", []);
  const race = nextRaceFor(athlete.id);
  return `
    <section class="card family-hero"><p class="eyebrow">EGE'NİN HAFTASI</p><h2>İstikrarlı ve hedefte.</h2><p>Katılımı yüksek, fiziksel gelişimi planla uyumlu. Koçu bu hafta start kararlarına odaklanıyor.</p></section>
    <div class="section-head"><div><h2>Haftalık özet</h2><p>Sadece çocuğunuza ait güncel bilgiler</p></div></div>
    <section class="grid stats-grid">
      ${statCard("Katılım", "3 / 3", "✓", "%100", "green")}
      ${statCard("Antrenman", "5,5 sa", "⌁", "+45 dk", "sea")}
      ${statCard("Gelişim", "Olumlu", "↗", "+4 puan", "")}
      ${statCard("Yarışa kalan", race ? `${daysUntil(race.startDate)} gün` : "—", "⚑", race?.sailingClass || "Atanmadı", "warn")}
    </section>
    <section class="grid two-col" style="margin-top:16px">
      <article class="card panel"><div class="panel-title"><h3>Antrenör değerlendirmesi</h3></div><p style="color:var(--muted);line-height:1.7">Ege, bu hafta tüm antrenmanlara katıldı. Teknik çalışmalarda odağını korudu ve fiziksel programını eksiksiz tamamladı.</p><button class="primary-button" data-route="family">Haftalık özeti görüntüle</button></article>
      <article class="card panel"><div class="panel-title"><h3>Sıradaki yarış</h3></div>${race ? `<h3>${race.title}</h3><p style="color:var(--muted);font-size:12px">${race.location} · ${new Date(race.startDate).toLocaleDateString("tr-TR")}</p>${progressRow("Hazırlık", 84)}` : `<p style="color:var(--muted)">Henüz yarış atanmamış.</p>`}</article>
    </section>`;
}

function athletesPage() {
  const filtered = data.athletes.filter(a =>
    (athleteFilter === "Tümü" || a.sailingClass.includes(athleteFilter) || (athleteFilter === "Düşük katılım" && a.attendance < 75)) &&
    `${a.firstName} ${a.lastName}`.toLocaleLowerCase("tr").includes(searchTerm.toLocaleLowerCase("tr"))
  );
  return `
    <div class="toolbar"><label class="search"><span>⌕</span><input id="athleteSearch" value="${searchTerm}" placeholder="Sporcu ara..." /></label><button class="primary-button" id="addAthlete">Sporcu ekle</button></div>
    <div class="filters">${["Tümü","Optimist","ILCA","420","Düşük katılım"].map(f => `<button class="filter-chip ${athleteFilter === f ? "active" : ""}" data-filter="${f}">${f}</button>`).join("")}</div>
    <section class="grid athlete-grid">
      ${filtered.length ? filtered.map(athleteCard).join("") : `<div class="card empty">Bu filtreye uygun sporcu bulunamadı.</div>`}
    </section>`;
}

function athleteCard(a) {
  const race = nextRaceFor(a.id);
  return `<article class="card athlete-card" data-athlete="${a.id}">
    <div class="athlete-head"><div class="athlete-avatar">${initials(a)}</div><div><h3>${a.firstName} ${a.lastName}</h3><p>${age(a.birthDate)} yaş · ${a.sailingClass}</p></div><span class="status-dot"></span></div>
    <div class="metric-pair"><div class="mini-metric"><small>KATILIM</small><strong>%${a.attendance}</strong></div><div class="mini-metric"><small>SON ÖLÇÜM</small><strong>${a.weightKg} kg</strong></div></div>
    <div class="athlete-footer"><span class="race-pill">${race ? `⚑ ${daysUntil(race.startDate)} gün · ${race.title.split(" ").slice(0,2).join(" ")}` : "Yarış atanmamış"}</span><span class="score">${a.score}</span></div>
  </article>`;
}

function racesPage() {
  const canManage = ["coach", "admin"].includes(data.currentRole);
  return `<div class="toolbar"><div><p style="margin:0;color:var(--muted);font-size:13px">${data.races.length} planlı yarış · ${new Set(data.races.flatMap(r => r.assignedAthleteIds)).size} sporcu</p></div>${canManage ? `<button class="primary-button" id="addRace">Yarış ekle</button>` : ""}</div>
    <section class="race-list">
      ${data.races.sort((a,b) => new Date(a.startDate)-new Date(b.startDate)).map(r => {
        const d = new Date(r.startDate);
        return `<article class="card race-item"><div class="date-tile"><span>${d.toLocaleDateString("tr-TR",{month:"short"}).toUpperCase()}</span><strong>${d.getDate()}</strong></div><div><h3>${r.title}</h3><p>⚑ ${r.location} · ${r.sailingClass} · ${r.assignedAthleteIds.length} sporcu</p></div><div class="race-actions"><span class="race-state">${daysUntil(r.startDate)} gün kaldı</span>${canManage ? `<button class="text-button" data-race-result="${r.id}">Sonuç gir</button>` : ""}</div></article>`;
      }).join("")}
    </section>`;
}

function progressPage() {
  return `<section class="grid stats-grid">
    ${statCard("Takım gelişim skoru", "83", "↗", "+5 puan", "green")}
    ${statCard("Ortalama fitness", "%84", "⌁", "+%7", "sea")}
    ${statCard("Riskli sporcu", "2", "!", "Takip gerekli", "warn")}
    ${statCard("Ölçüm güncelliği", "%78", "⚖", "3 eksik", "")}
  </section>
  <section class="grid two-col" style="margin-top:16px">
    <article class="card panel"><div class="panel-title"><h3>Son 6 ay takım gelişimi</h3><span class="race-state">+%14</span></div>${lineChart()}</article>
    <article class="card panel"><div class="panel-title"><h3>Öncelik listesi</h3></div><div class="activity-list">${activity("!", "Can Özer", "%69 katılım · son 30 gün düşüş", "Risk")}${activity("⚖", "Bora Çelik", "Ölçüm girişi 52 gün önce", "Eksik")}${activity("↗", "Lara Akın", "Gelişim skoru +8 puan", "Güçlü")}</div></article>
  </section>
  <div class="section-head"><div><h2>Sporcu bazlı hazırlık</h2><p>Katılım, fitness ve yarış performansının sade bileşik görünümü</p></div></div>
  <section class="card panel"><div class="progress-list">${data.athletes.map(a => progressRow(`${a.firstName} ${a.lastName}`, a.score, `${a.sailingClass} · Fitness %${a.fitness}`)).join("")}</div></section>`;
}

function lineChart() {
  return `<div class="chart"><svg viewBox="0 0 500 160" preserveAspectRatio="none"><defs><linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#18b7b2"/><stop offset="100%" stop-color="#18b7b2" stop-opacity="0"/></linearGradient></defs><line class="chart-grid" x1="0" y1="40" x2="500" y2="40"/><line class="chart-grid" x1="0" y1="90" x2="500" y2="90"/><line class="chart-grid" x1="0" y1="140" x2="500" y2="140"/><path class="chart-area" d="M0 130 L80 117 L165 121 L250 86 L335 73 L420 44 L500 29 L500 160 L0 160Z"/><path class="chart-line" d="M0 130 L80 117 L165 121 L250 86 L335 73 L420 44 L500 29"/></svg></div>`;
}

function familyPage() {
  const a = data.athletes[0];
  if (!a) return onboardingEmptyState("Özet oluşturmak için sporcu gerekli", "Önce bir sporcu ekleyin veya hesabınıza sporcu bağlanmasını bekleyin.", [["athletes", "Sporculara git"]]);
  const race = nextRaceFor(a.id);
  const canSendEmail = appConfig.demoMode || appConfig.parentSummaryEmailsEnabled;
  const emailNotice = canSendEmail
    ? "E-posta gönderimi aktif. Göndermeden önce taslağı kontrol edin."
    : "Taslak sistemi hazır. Gerçek e-posta gönderimi, sağlayıcı ve Firebase Functions açılınca aktif edilecek.";
  return `<div class="toolbar summary-toolbar"><div><p style="margin:0;color:var(--muted);font-size:13px">${emailNotice}</p></div><button class="primary-button summary-send-button" id="sendSummary" ${canSendEmail ? "" : "data-email-disabled=\"true\""}>Taslağı gönder</button></div>
    <section class="card summary-preview"><div class="email-paper">
      <p class="eyebrow">HAFTALIK GELİŞİM ÖZETİ</p><h3>${a.firstName} ${a.lastName} · 17–23 Haziran</h3>
      <p>Merhaba Selin Hanım,</p><p>Ege bu hafta tüm planlı çalışmalarına katıldı ve özellikle start pozisyonu çalışmalarında belirgin ilerleme gösterdi.</p>
      <div class="email-stats"><div><strong>3 / 3</strong><span>ANTRENMAN</span></div><div><strong>5,5 sa</strong><span>TOPLAM SÜRE</span></div><div><strong>%86</strong><span>FİTNESS</span></div></div>
      <p><strong>Fiziksel gelişim:</strong> Son ölçümlere göre boy/kilo dengesi yaş ve çalışma programıyla uyumludur. Gereksiz sağlık verisi paylaşılmamıştır.</p>
      <p><strong>Yaklaşan yarış:</strong> ${race ? `${race.title}, ${race.location}. Yarışa <strong>${daysUntil(race.startDate)} gün</strong> kaldı.` : "Henüz yarış atanmamış."}</p>
      <p><strong>Gelecek hafta odağı:</strong> Start kararları, dönüşlerde hız koruma ve core stabilizasyonu.</p>
      <p>Sevgiler,<br><strong>Deniz Arman</strong><br>İstanbul Yelken Akademi</p>
    </div></section>`;
}

function render() {
  if (!appConfig.demoMode) {
    if (sessionStatus === "loading") {
      loadingPage();
      return;
    }
    if (sessionStatus === "signed-out") {
      productionLoginPage();
      return;
    }
    if (sessionStatus === "email-unverified") {
      emailVerificationPage();
      return;
    }
    if (sessionStatus === "pending") {
      pendingAccountPage();
      return;
    }
    if (sessionStatus === "denied") {
      accessDeniedPage();
      return;
    }
    if (sessionStatus === "workspace-error") {
      workspaceErrorPage();
      return;
    }
  }
  if (!data.loggedIn) {
    demoLoginPage();
    return;
  }
  if (!allowedRoutes[data.currentRole].includes(route)) route = "dashboard";
  if (route === "dashboard") {
    const views = { coach: coachDashboard, athlete: athleteDashboard, parent: parentDashboard, admin: coachDashboard };
    layout(views[data.currentRole](), data.currentRole === "coach" ? "Günaydın, Deniz" : data.currentRole === "parent" ? "Ege’nin gelişimi" : data.currentRole === "athlete" ? "Benim rotam" : "Kulüp görünümü");
  } else if (route === "athletes") layout(athletesPage(), "Sporcular");
  else if (route === "races") layout(racesPage(), "Yarış takvimi");
  else if (route === "progress") layout(progressPage(), "Gelişim merkezi");
  else if (route === "family") layout(familyPage(), "Aile bilgilendirme");
  else if (route === "applications") layout(applicationsPage(), "Üyelik başvuruları");
  else layout(accountPage(), "Hesabım");
  bindPageEvents();
}

function bindPageEvents() {
  document.querySelectorAll("[data-filter]").forEach(btn => btn.addEventListener("click", () => { athleteFilter = btn.dataset.filter; render(); }));
  document.querySelector("#athleteSearch")?.addEventListener("input", e => { searchTerm = e.target.value; setTimeout(render, 150); });
  document.querySelector("#addAthlete")?.addEventListener("click", openAthleteModal);
  document.querySelector("#addRace")?.addEventListener("click", openRaceModal);
  document.querySelector("#sendSummary")?.addEventListener("click", async () => {
    if (document.querySelector("#sendSummary")?.dataset.emailDisabled === "true") {
      showToast("E-posta gönderimi için Blaze planı ve gönderici adresi bağlanınca bu düğme açılacak.");
      return;
    }
    const summary = data.parentSummaries.find(item => ["draft", "failed"].includes(item.status));
    if (!summary) {
      showToast("Gönderilmeye hazır bir veli özeti taslağı bulunamadı.");
      return;
    }
    if (appConfig.demoMode) {
      summary.status = "sent";
      summary.sentAt = new Date().toISOString();
      saveData(data);
      showToast("Özet demo gönderim geçmişine kaydedildi");
      return;
    }
    try {
      const functionsApi = await import("./functions-service.js");
      await functionsApi.sendParentSummary(summary.id);
      summary.status = "sent";
      showToast("Aile özeti güvenli şekilde gönderildi.");
      render();
    } catch (error) {
      captureError(error, "parent-summary");
      showToast("Özet gönderilemedi. Veli bağlantısını ve e-posta ayarlarını kontrol edin.");
    }
  });
  document.querySelectorAll("[data-race-result]").forEach(button => button.addEventListener("click", () => openRaceResultModal(button.dataset.raceResult)));
  document.querySelectorAll("[data-athlete]").forEach(card => card.addEventListener("click", () => openAthleteDetail(card.dataset.athlete)));
  document.querySelectorAll("[data-data-request]").forEach(button => button.addEventListener("click", () => submitDataRequest(button.dataset.dataRequest)));
  document.querySelector("#familySummaryPreference")?.addEventListener("change", async event => {
    const enabled = event.target.checked;
    if (appConfig.demoMode) {
      currentProfile = {
        ...(currentProfile || {}),
        communicationPreferences: { familySummaries: enabled }
      };
      showToast(enabled ? "Aile özeti e-postaları açıldı." : "Aile özeti e-postaları kapatıldı.");
      return;
    }
    try {
      await repositoryApi.updateFamilySummaryPreference(currentProfile, enabled);
      currentProfile = {
        ...currentProfile,
        communicationPreferences: { familySummaries: enabled }
      };
      data.users = data.users.map(user => user.id === currentProfile.id ? currentProfile : user);
      showToast(enabled ? "Aile özeti e-postaları açıldı." : "Aile özeti e-postaları kapatıldı.");
    } catch (error) {
      captureError(error, "communication-preferences");
      event.target.checked = !enabled;
      showToast("İletişim tercihi güncellenemedi.");
    }
  });
  document.querySelectorAll("[data-copy-invite]").forEach(button => {
    button.addEventListener("click", async () => {
      const code = button.dataset.inviteCode;
      if (!code) {
        showToast("Önce kulüp davet kodu oluşturulmalı.");
        return;
      }
      const text = button.dataset.copyInvite === "message"
        ? `SailForce üyelik bağlantısı: ${appConfig.appUrl}/\nKulüp davet kodu: ${code}`
        : code;
      try {
        await copyText(text);
        showToast(button.dataset.copyInvite === "message" ? "Üyelik davet metni kopyalandı." : "Kulüp davet kodu kopyalandı.");
      } catch (error) {
        captureError(error, "invite-code-copy");
        showToast(`Kopyalama yapılamadı. Kod: ${code}`);
      }
    });
  });
  document.querySelector("#createInviteCode")?.addEventListener("click", async () => {
    if (data.currentRole !== "admin") {
      showToast("Davet kodunu yalnızca kulüp admini oluşturabilir.");
      return;
    }
    if (appConfig.demoMode) {
      const code = `SF${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      data.inviteCodes = [{ id: code, code, status: "active", allowedRoles: ["coach", "athlete", "parent"] }, ...(data.inviteCodes || [])];
      showToast("Demo davet kodu oluşturuldu.");
      render();
      return;
    }
    try {
      const invite = await repositoryApi.createClubInviteCode(currentProfile);
      data.inviteCodes = [invite, ...(data.inviteCodes || [])];
      showToast(`Yeni davet kodu oluşturuldu: ${invite.code}`);
      render();
    } catch (error) {
      captureError(error, "invite-code-create");
      showToast("Davet kodu oluşturulamadı. Yetkinizi ve bağlantınızı kontrol edin.");
    }
  });
  document.querySelectorAll("[data-deactivate-invite]").forEach(button => {
    button.addEventListener("click", async () => {
      const code = button.dataset.deactivateInvite;
      if (!code) return;
      if (!window.confirm(`${code} davet kodu devre dışı bırakılsın mı? Bu kodla yeni başvuru alınmaz.`)) return;
      if (appConfig.demoMode) {
        data.inviteCodes = (data.inviteCodes || []).map(invite =>
          invite.code === code ? { ...invite, status: "inactive" } : invite
        );
        showToast("Demo davet kodu devre dışı bırakıldı.");
        render();
        return;
      }
      try {
        await repositoryApi.deactivateClubInviteCode(currentProfile, code);
        data.inviteCodes = (data.inviteCodes || []).map(invite =>
          invite.code === code ? { ...invite, status: "inactive" } : invite
        );
        showToast(`${code} davet kodu devre dışı bırakıldı.`);
        render();
      } catch (error) {
        captureError(error, "invite-code-deactivate");
        showToast("Davet kodu devre dışı bırakılamadı.");
      }
    });
  });
  document.querySelector("#subscriptionInterest")?.addEventListener("click", async () => {
    if (!["admin", "coach"].includes(currentProfile?.role || data.currentRole)) {
      showToast("Plan talepleri kulüp yöneticisi veya antrenör üzerinden alınır.");
      return;
    }
    if (appConfig.demoMode) {
      showToast("Plan yükseltme ilgisi demo modunda örnek olarak kaydedildi.");
      return;
    }
    try {
      await repositoryApi.createSubscriptionInterest(currentProfile, entitlements.id, "club");
      showToast("Plan yükseltme talebiniz kaydedildi. Ödeme altyapısı bağlanınca bu akış tamamlanacak.");
    } catch (error) {
      captureError(error, "subscription-interest");
      showToast("Plan talebi kaydedilemedi. Lütfen tekrar deneyin.");
    }
  });
  document.querySelectorAll("[data-application-action]").forEach(button => {
    button.addEventListener("click", () => handleMembershipApplication(
      button.dataset.userId,
      button.dataset.applicationAction
    ));
  });
  document.querySelectorAll("[data-request-status]").forEach(button => {
    button.addEventListener("click", () => handleDataRequestStatus(
      button.dataset.requestId,
      button.dataset.requestStatus
    ));
  });
}

function clubInviteCodeCard() {
  const inviteCodes = data.inviteCodes?.length
    ? data.inviteCodes
    : (currentProfile?.clubJoinCode ? [{
        id: currentProfile.clubJoinCode,
        code: currentProfile.clubJoinCode,
        status: "active",
        allowedRoles: ["coach", "athlete", "parent"]
      }] : []);
  const primaryCode = inviteCodes.find(item => item.status === "active")?.code || "";
  const roleLabels = { coach: "Antrenör", athlete: "Sporcu", parent: "Veli" };
  return `<article class="card panel invite-code-card">
    <div class="panel-title">
      <div>
        <h3>Kulüp davet kodu</h3>
        <p class="muted-copy">Yeni üyeler bu kodla başvurur; sadece bu kodla gelen başvurular burada görünür.</p>
      </div>
      <span class="race-state">${inviteCodes.length ? `${inviteCodes.length} kod` : "eksik"}</span>
    </div>
    <div class="invite-code-box">
      <strong>${primaryCode || "Kod oluşturulmadı"}</strong>
      <span>Üye adayı sailforce.net üzerinden “Üye ol” ekranında bu kodu girer. Aktif kod yoksa yeni başvuru alınmaz.</span>
    </div>
    ${inviteCodes.length ? `<div class="invite-code-list">
      ${inviteCodes.slice(0, 6).map(invite => `<div class="invite-code-row">
        <div><strong>${invite.code}</strong><span>${(invite.allowedRoles || []).map(role => roleLabels[role] || role).join(", ") || "Tüm roller"}</span></div>
        <span class="race-state">${invite.status || "active"}</span>
        ${invite.status === "active" ? `<button class="text-button" data-deactivate-invite="${invite.code}">Devre dışı bırak</button>` : ""}
      </div>`).join("")}
    </div>` : ""}
    <div class="application-actions invite-actions">
      <button class="secondary-button" data-copy-invite="code" data-invite-code="${primaryCode}" ${primaryCode ? "" : "disabled"}>Kodu kopyala</button>
      <button class="primary-button" data-copy-invite="message" data-invite-code="${primaryCode}" ${primaryCode ? "" : "disabled"}>Davet metnini kopyala</button>
      <button class="secondary-button" id="createInviteCode">Yeni kod oluştur</button>
    </div>
  </article>`;
}

const requiredApplicationConsentKeys = ["kvkkNotice", "privacy", "terms", "explicitConsent"];

function applicationConsentStatus(application) {
  const legalConsents = application.legalConsents || {};
  const requiredReady = requiredApplicationConsentKeys.every(key =>
    legalConsents[key]?.accepted === true
  );
  return {
    requiredReady,
    emailCommunication: legalConsents.emailCommunication?.accepted === true
  };
}

function applicationReviewBadges(application) {
  const consentStatus = applicationConsentStatus(application);
  return `<div class="application-review">
    <span class="mini-status ${application.emailVerified ? "success" : "warn"}">${application.emailVerified ? "E-posta doğrulandı" : "E-posta bekleniyor"}</span>
    <span class="mini-status ${consentStatus.requiredReady ? "success" : "warn"}">${consentStatus.requiredReady ? "KVKK tamam" : "KVKK eksik"}</span>
    <span class="mini-status ${consentStatus.emailCommunication ? "success" : "muted"}">${consentStatus.emailCommunication ? "Duyuru izni var" : "Duyuru izni yok"}</span>
  </div>`;
}

function applicationsPage() {
  const applications = data.pendingUsers || [];
  if (!applications.length) {
    return `${clubInviteCodeCard()}${onboardingEmptyState(
      "Bekleyen üyelik başvurusu yok",
      "Yeni başvuru geldiğinde burada görünecek. Şimdilik davet kodunu kopyalayıp üye adaylarına gönderebilirsiniz.",
      []
    )}`;
  }
  const roleLabels = { coach: "Antrenör", athlete: "Sporcu", parent: "Aile / Veli" };
  return `${clubInviteCodeCard()}<section class="card panel">
    <div class="panel-title"><div><h3>Bekleyen başvurular</h3><p class="muted-copy">Yalnızca kulüp davet kodunuzla başvuran hesaplar gösterilir.</p></div><span class="race-state">${applications.length}</span></div>
    <div class="application-list">
      ${applications.map(application => `<article class="application-row">
        <div><strong>${application.fullName}</strong><span>${application.email}</span>${applicationReviewBadges(application)}</div>
        <span class="role-badge">${roleLabels[application.requestedRole] || "Başvuru"}</span>
        ${["athlete", "parent"].includes(application.requestedRole) ? `<select class="application-athlete-select" data-application-athlete="${application.id}">
          <option value="">Sporcu seçin</option>
          ${data.athletes.map(athlete => `<option value="${athlete.id}">${athlete.firstName} ${athlete.lastName}</option>`).join("")}
        </select>` : ""}
        <div class="application-actions">
          <button class="secondary-button" data-application-action="deny" data-user-id="${application.id}">Reddet</button>
          <button class="primary-button" data-application-action="approve" data-user-id="${application.id}">Onayla</button>
        </div>
      </article>`).join("")}
    </div>
  </section>`;
}

async function handleMembershipApplication(userId, action) {
  const application = data.pendingUsers.find(item => item.id === userId);
  if (!application) return;
  const linkAthleteId = document.querySelector(`[data-application-athlete="${userId}"]`)?.value || "";
  const consentStatus = applicationConsentStatus(application);
  if (action === "approve" && application.emailVerified !== true) {
    showToast("Onaylamadan önce kullanıcı e-posta adresini doğrulamalı.");
    return;
  }
  if (action === "approve" && !consentStatus.requiredReady) {
    showToast("Zorunlu KVKK ve üyelik onayları tamamlanmadan hesap açılamaz.");
    return;
  }
  if (action === "approve" && ["athlete", "parent"].includes(application.requestedRole) && !linkAthleteId) {
    showToast("Onaylamadan önce bağlanacak sporcuyu seçin.");
    return;
  }
  if (!window.confirm(`${application.fullName} başvurusu ${action === "approve" ? "onaylansın" : "reddedilsin"} mı?`)) return;
  try {
    await repositoryApi.resolveMembershipApplication(currentProfile, application, action, linkAthleteId);
    data.pendingUsers = data.pendingUsers.filter(item => item.id !== userId);
    render();
    showToast(action === "approve" ? "Üyelik etkinleştirildi." : "Başvuru reddedildi.");
  } catch (error) {
    captureError(error, "membership-application");
    showToast("Başvuru güncellenemedi.");
  }
}

function onboardingEmptyState(title, message, actions) {
  return `<section class="card onboarding-empty"><div class="state-icon">⛵</div><h2>${title}</h2><p>${message}</p><div class="empty-actions">${actions.map(([target, label]) => `<button class="primary-button" data-route="${target}">${label}</button>`).join("")}</div></section>`;
}

function readinessRow(label, ready, detail) {
  return `<div class="readiness-row">
    <div><strong>${label}</strong><span>${detail}</span></div>
    <span class="race-state ${ready ? "success" : "warn"}">${ready ? "hazır" : "bekliyor"}</span>
  </div>`;
}

function productionReadinessPanel() {
  const liveMode = !appConfig.demoMode;
  const appCheckReady = Boolean(appConfig.recaptchaEnterpriseSiteKey);
  const emailReady = Boolean(appConfig.parentSummaryEmailsEnabled);
  return `<article class="card panel production-readiness-card">
    <div class="panel-title">
      <div>
        <h3>Üretim hazırlığı</h3>
        <p class="muted-copy">Canlı pilotta açık/kapalı kritik parçaları tek yerden izleyin.</p>
      </div>
      <span class="race-state">v${appVersion}</span>
    </div>
    <div class="readiness-list">
      ${readinessRow("Canlı veri modu", liveMode, liveMode ? "Demo kapalı, Firebase veri katmanı kullanılıyor." : "Demo açık; canlı kullanıcı almadan önce kapatılmalı.")}
      ${readinessRow("Firebase bağlantısı", appConfig.firebaseConfigured, appConfig.firebaseConfigured ? "Web uygulaması Firebase projesine bağlı." : "Firebase web anahtarları eksik.")}
      ${readinessRow("App Check anahtarı", appCheckReady, appCheckReady ? "reCAPTCHA Enterprise anahtarı istemciye tanımlı." : "Anahtar tanımlanınca kötüye kullanıma karşı zorlamayı aşamalı açabiliriz.")}
      ${readinessRow("Aile e-postaları", emailReady, emailReady ? "Gönderim düğmesi canlıda aktif." : "Taslaklar hazır; Blaze, Resend ve Functions yayını bekleniyor.")}
      ${readinessRow("Başvuru onay kapısı", true, "E-posta doğrulanmadan ve zorunlu KVKK/onay kayıtları tamamlanmadan üyelik aktif edilemez.")}
      ${readinessRow("Canlı adres", appConfig.appUrl === "https://sailforce.net", appConfig.appUrl || window.location.origin)}
    </div>
  </article>`;
}

function familyCommunicationPanel(profile) {
  const enabled = profile.communicationPreferences?.familySummaries !== false;
  return `<article class="card panel communication-card">
    <div class="panel-title">
      <div>
        <h3>Aile bilgilendirme tercihleri</h3>
        <p class="muted-copy">Çocuğunuzla ilgili aile özeti e-postalarını istediğiniz zaman kapatıp açabilirsiniz.</p>
      </div>
      <span class="race-state">${enabled ? "açık" : "kapalı"}</span>
    </div>
    <label class="preference-toggle">
      <input id="familySummaryPreference" type="checkbox" ${enabled ? "checked" : ""} />
      <span>
        <strong>Aile özeti e-postaları</strong>
        <small>${enabled ? "Haftalık/yarış bilgilendirme özetleri gönderilebilir." : "Yeni aile özeti e-postası gönderilmez."}</small>
      </span>
    </label>
    <p class="muted-copy">Bu tercih KVKK/açık rıza kayıtlarını silmez; yalnızca operasyonel aile özeti gönderimini durdurur.</p>
  </article>`;
}

function accountPage() {
  const profile = currentProfile || data.users.find(user => user.role === data.currentRole) || {};
  const errorPanel = data.currentRole === "admin" ? `<article class="card panel">
    <div class="panel-title"><h3>Sistem durumu</h3><span class="race-state">${data.systemErrors?.length || 0} kayıt</span></div>
    ${data.systemErrors?.length
      ? `<div class="activity-list">${data.systemErrors.slice(0, 5).map(error => activity(
          "!",
          error.context || "Uygulama hatası",
          error.message || "Ayrıntı bulunamadı",
          error.status || "new"
        )).join("")}</div>`
      : `<p class="muted-copy">Son hata kaydı bulunmuyor. Sağlık kontrolü <code>/health.json</code> adresinde yayındadır.</p>`}
  </article>` : "";
  const requestPanel = data.currentRole === "admin" ? `<article class="card panel">
    <div class="panel-title"><h3>KVKK veri talepleri</h3><span class="race-state">${data.dataRequests?.length || 0}</span></div>
    ${data.dataRequests?.length
      ? `<div class="application-list">${data.dataRequests.map(request => `<article class="application-row">
          <div><strong>${request.requestType === "delete" ? "Hesap silme" : "Veri dışa aktarma"}</strong><span>${request.userId} · ${request.status}</span></div>
          <div class="application-actions">
            ${request.status === "requested" ? `<button class="secondary-button" data-request-id="${request.id}" data-request-status="reviewing">İncelemeye al</button>` : ""}
            ${request.status !== "completed" ? `<button class="primary-button" data-request-id="${request.id}" data-request-status="completed">Tamamlandı</button>` : ""}
          </div>
        </article>`).join("")}</div>`
      : `<p class="muted-copy">Bekleyen veri talebi bulunmuyor.</p>`}
  </article>` : "";
  const subscriptionInterestPanel = ["admin", "coach"].includes(data.currentRole) ? `<article class="card panel">
    <div class="panel-title"><h3>Plan talepleri</h3><span class="race-state">${data.subscriptionInterests?.length || 0}</span></div>
    ${data.subscriptionInterests?.length
      ? `<div class="activity-list">${data.subscriptionInterests.slice(0, 5).map(interest => activity(
          "↗",
          `${interest.currentPlanId || "free"} → ${interest.requestedPlanId || "club"}`,
          interest.userId === currentProfile?.id ? "Sizin talebiniz" : `Talep sahibi: ${interest.userId}`,
          interest.status || "requested"
        )).join("")}</div>`
      : `<p class="muted-copy">Henüz plan yükseltme talebi yok. “Planları incele” düğmesi ödeme altyapısı bağlanana kadar talebi güvenli şekilde kaydeder.</p>`}
  </article>` : "";
  const invitePanel = data.currentRole === "admin" ? clubInviteCodeCard() : "";
  const readinessPanel = data.currentRole === "admin" ? productionReadinessPanel() : "";
  const communicationPanel = data.currentRole === "parent" ? familyCommunicationPanel(profile) : "";
  return `<section class="grid two-col">
    <article class="card panel">
      <div class="panel-title"><h3>Hesap bilgileri</h3><span class="race-state">${profile.status || "demo"}</span></div>
      <dl class="profile-list">
        <div><dt>Ad soyad</dt><dd>${profile.fullName || "Demo kullanıcı"}</dd></div>
        <div><dt>E-posta</dt><dd>${profile.email || "—"}</dd></div>
        <div><dt>Rol</dt><dd>${profile.role || data.currentRole}</dd></div>
        <div><dt>Kulüp</dt><dd>${profile.clubId || "Demo kulüp"}</dd></div>
        <div><dt>Uygulama sürümü</dt><dd>v${appVersion}</dd></div>
      </dl>
    </article>
    <article class="card panel">
      <div class="panel-title"><h3>Abonelik</h3><span class="race-state">${entitlements.status}</span></div>
      <p class="muted-copy"><strong>${entitlements.name}</strong> planı · ${data.athletes.length}/${entitlements.maxAthletes} sporcu kapasitesi.</p>
      <button class="secondary-button" id="subscriptionInterest">Planları incele</button>
    </article>
    <article class="card panel">
      <div class="panel-title"><h3>Veri haklarınız</h3></div>
      <p class="muted-copy">Verilerinizin bir kopyasını isteyebilir veya hesabınızın ve ilişkili kişisel verilerinizin silinmesini talep edebilirsiniz. Talepler kimlik ve yetki kontrolünden sonra işlenir.</p>
      <div class="privacy-actions">
        <button class="secondary-button" data-data-request="export">Verilerimi dışa aktarma talebi</button>
        <button class="danger-button" data-data-request="delete">Hesap silme talebi</button>
      </div>
      <div class="legal-links account-legal"><a href="/kvkk.html">KVKK Aydınlatma Metni</a><a href="/privacy.html">Gizlilik Politikası</a><a href="/terms.html">Kullanım Koşulları</a></div>
    </article>
    ${communicationPanel}
    ${readinessPanel}
    ${errorPanel}
    ${invitePanel}
    ${subscriptionInterestPanel}
    ${requestPanel}
  </section>`;
}

async function handleDataRequestStatus(requestId, status) {
  try {
    await repositoryApi.updateDataRequest(currentProfile, requestId, status);
    const request = data.dataRequests.find(item => item.id === requestId);
    if (request) request.status = status;
    render();
    showToast("Veri talebi güncellendi.");
  } catch (error) {
    captureError(error, "data-request");
    showToast("Veri talebi güncellenemedi.");
  }
}

async function submitDataRequest(type) {
  const labels = { export: "Veri dışa aktarma", delete: "Hesap silme" };
  if (appConfig.demoMode) {
    showToast(`${labels[type]} talebi demo modunda kaydedilmez.`);
    return;
  }
  if (type === "delete" && !window.confirm("Hesap silme talebi oluşturulsun mu? Bu işlem hesabınızı hemen silmez; yönetici incelemesine gönderir.")) return;
  try {
    await repositoryApi.createDataRequest(currentProfile, type);
    showToast(`${labels[type]} talebiniz alındı.`);
  } catch (error) {
    captureError(error, "data-request");
    showToast("Talep oluşturulamadı. Lütfen tekrar deneyin.");
  }
}

function modal(content) {
  const wrap = document.createElement("div");
  wrap.className = "modal-backdrop";
  wrap.innerHTML = `<div class="modal">${content}</div>`;
  document.body.appendChild(wrap);
  wrap.addEventListener("click", e => { if (e.target === wrap || e.target.closest("[data-close]")) wrap.remove(); });
  return wrap;
}

function openAthleteModal() {
  if (!canAddAthlete(entitlements, data.athletes.length)) {
    showToast(`${entitlements.name} planındaki ${entitlements.maxAthletes} sporcu limitine ulaştınız.`);
    return;
  }
  const m = modal(`<div class="modal-head"><div><h2>Yeni sporcu</h2><p>Temel profil bilgileri</p></div><button class="close-button" data-close>×</button></div>
    <form id="athleteForm"><div class="form-grid">
      <div class="field"><label>Ad</label><input name="firstName" required /></div><div class="field"><label>Soyad</label><input name="lastName" required /></div>
      <div class="field"><label>Doğum tarihi</label><input name="birthDate" type="date" required /></div><div class="field"><label>Yelken sınıfı</label><select name="sailingClass"><option>Optimist</option><option>ILCA 4</option><option>420</option><option>J70</option></select></div>
      <div class="field"><label>Veli adı</label><input name="parentName" required /></div><div class="field"><label>Durum</label><select name="status"><option value="active">Aktif</option><option value="passive">Pasif</option></select></div>
    </div><div class="modal-actions"><button type="button" class="secondary-button" data-close>Vazgeç</button><button class="primary-button">Sporcuyu kaydet</button></div></form>`);
  m.querySelector("form").addEventListener("submit", async e => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.target));
    try {
      const athlete = appConfig.demoMode
        ? { id: crypto.randomUUID(), ...f, attendance: 0, fitness: 0, score: 0, weightKg: "-", heightCm: "-", coachId: "u1" }
        : await repositoryApi.createAthlete(currentProfile, f);
      data.athletes.push(athlete);
      if (appConfig.demoMode) saveData(data);
      m.remove();
      render();
      showToast("Sporcu kaydedildi");
    } catch (error) {
      captureError(error, "athlete-create");
      showToast("Sporcu kaydedilemedi. Yetkinizi ve bağlantınızı kontrol edin.");
    }
  });
}

function openRaceModal() {
  if (!hasFeature(entitlements, "races")) {
    showToast("Yarış yönetimi Antrenör Pro ve Kulüp planlarında kullanılabilir.");
    return;
  }
  const m = modal(`<div class="modal-head"><div><h2>Yeni hedef yarış</h2><p>Takım takvimine yarış ekle</p></div><button class="close-button" data-close>×</button></div>
    <form><div class="form-grid">
      <div class="field full"><label>Yarış adı</label><input name="title" required /></div>
      <div class="field"><label>Lokasyon</label><input name="location" required /></div><div class="field"><label>Yelken sınıfı</label><select name="sailingClass"><option>Optimist</option><option>ILCA 4</option><option>420</option></select></div>
      <div class="field"><label>Başlangıç</label><input name="startDate" type="date" required /></div><div class="field"><label>Bitiş</label><input name="endDate" type="date" required /></div>
      <div class="field"><label>Hedef seviyesi</label><select name="targetLevel"><option>Hazırlık</option><option>Bölgesel</option><option>Ulusal</option><option>Uluslararası</option></select></div><div class="field"><label>Yarış hedefi</label><input name="goal" placeholder="Örn. İlk 10" /></div>
    </div><div class="modal-actions"><button type="button" class="secondary-button" data-close>Vazgeç</button><button class="primary-button">Yarışı kaydet</button></div></form>`);
  m.querySelector("form").addEventListener("submit", async e => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.target));
    try {
      const race = appConfig.demoMode
        ? { id: crypto.randomUUID(), ...f, assignedAthleteIds: [], status: "planned" }
        : await repositoryApi.createRace(currentProfile, f);
      data.races.push(race);
      if (appConfig.demoMode) saveData(data);
      m.remove();
      render();
      showToast("Yarış takvime eklendi");
    } catch (error) {
      captureError(error, "race-create");
      showToast("Yarış kaydedilemedi. Yetkinizi ve bağlantınızı kontrol edin.");
    }
  });
}

function openRaceResultModal(raceId) {
  const race = data.races.find(item => item.id === raceId);
  const assignedAthletes = data.athletes.filter(athlete => race.assignedAthleteIds.includes(athlete.id));
  const athleteOptions = (assignedAthletes.length ? assignedAthletes : data.athletes)
    .map(athlete => `<option value="${athlete.id}">${athlete.firstName} ${athlete.lastName}</option>`)
    .join("");
  const m = modal(`<div class="modal-head"><div><p class="eyebrow">${race.sailingClass}</p><h2>Yarış sonucu</h2><p>${race.title}</p></div><button class="close-button" data-close>×</button></div>
    <form><div class="form-grid">
      <div class="field full"><label>Sporcu</label><select name="athleteId" required>${athleteOptions}</select></div>
      ${field("Genel sıralama", "overallRank", "number", true, "1", "1")}
      ${field("Kategori sıralaması", "categoryRank", "number", false, "1", "1")}
      ${field("Toplam katılımcı", "totalParticipants", "number", false, "1", "1")}
      ${field("Yarış sayısı", "raceCount", "number", false, "1", "1")}
      ${field("Net puan", "netPoints", "number", false, "0.1")}
      ${textareaField("Antrenör değerlendirmesi", "coachEvaluation", true)}
      ${textareaField("Güçlü yönler", "strengths")}
      ${textareaField("Gelişim alanları", "improvementAreas")}
      ${textareaField("Sonraki yarış hedefi", "nextRaceGoal")}
    </div><div class="modal-actions"><button type="button" class="secondary-button" data-close>Vazgeç</button><button class="primary-button">Sonucu kaydet</button></div></form>`);
  m.querySelector("form").addEventListener("submit", async event => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.target));
    const athleteId = values.athleteId;
    delete values.athleteId;
    values.raceId = race.id;
    values.raceTitle = race.title;
    values.sailingClass = race.sailingClass;
    try {
      const result = appConfig.demoMode
        ? { id: crypto.randomUUID(), athleteId, coachId: "u1", ...values }
        : await repositoryApi.createRaceResult(currentProfile, athleteId, values);
      data.raceResults.unshift(result);
      if (appConfig.demoMode) saveData(data);
      m.remove();
      render();
      showToast("Yarış sonucu kaydedildi");
    } catch (error) {
      captureError(error, "race-result");
      showToast("Yarış sonucu kaydedilemedi.");
    }
  });
}

function openAthleteDetail(id) {
  const a = data.athletes.find(x => x.id === id);
  const race = nextRaceFor(id);
  const canManage = ["coach", "admin"].includes(data.currentRole);
  const m = modal(`<div class="modal-head"><div><p class="eyebrow">${a.sailingClass}</p><h2>${a.firstName} ${a.lastName}</h2><p>${age(a.birthDate)} yaş · ${a.parentName || ""}</p></div><button class="close-button" data-close>×</button></div>
    <div class="grid stats-grid" style="grid-template-columns:repeat(3,1fr)">${statCard("Katılım", `%${a.attendance}`, "✓", "", "green")}${statCard("Fitness", `%${a.fitness}`, "⌁", "", "sea")}${statCard("Gelişim", a.score, "↗", "", "")}</div>
    ${canManage ? `<div class="quick-actions">
      <button class="secondary-button" data-record="measurement">⚖ Ölçüm</button>
      <button class="secondary-button" data-record="attendance">✓ Yoklama</button>
      <button class="secondary-button" data-record="workout">⌁ Fitness</button>
      <button class="secondary-button" data-record="summary">✉ Veli özeti</button>
    </div>` : ""}
    <div class="section-head"><div><h2>Fiziksel gelişim</h2><p>${a.heightCm} cm · ${a.weightKg} kg</p></div></div>
    ${lineChart()}
    <div class="card panel" style="box-shadow:none"><div class="panel-title"><h3>Hedef yarış</h3></div><strong>${race?.title || "Henüz atanmadı"}</strong><p style="color:var(--muted);font-size:12px">${race ? `${race.location} · ${daysUntil(race.startDate)} gün kaldı` : ""}</p></div>`);
  m.querySelectorAll("[data-record]").forEach(button => button.addEventListener("click", () => {
    m.remove();
    openRecordModal(button.dataset.record, a);
  }));
}

function openRecordModal(type, athlete) {
  const forms = {
    measurement: {
      title: "Fiziksel ölçüm ekle",
      collection: "measurements",
      fields: `
        ${field("Tarih", "date", "date", true)}
        ${field("Boy (cm)", "heightCm", "number", true, "0.1")}
        ${field("Kilo (kg)", "weightKg", "number", true, "0.1")}
        ${field("Yağ oranı (%)", "bodyFatPercent", "number", false, "0.1")}
        ${field("Kas (kg)", "muscleKg", "number", false, "0.1")}
        ${field("Dinlenik nabız", "restingHeartRate", "number")}
        ${textareaField("Antrenör notu", "coachNote")}`
    },
    attendance: {
      title: "Yoklama kaydı ekle",
      collection: "attendance",
      fields: `
        ${field("Tarih", "date", "date", true)}
        ${selectField("Antrenman tipi", "sessionType", ["Yelken","Fitness","Teori","Yarış","Kamp"])}
        ${selectField("Durum", "status", [["present","Geldi"],["absent","Gelmedi"],["excused","Mazeretli"]])}
        ${field("Süre (dk)", "durationMinutes", "number", true)}
        ${field("Geç kalma (dk)", "lateMinutes", "number")}
        ${textareaField("Mazeret / not", "coachNote")}`
    },
    workout: {
      title: "Fitness kaydı ekle",
      collection: "workoutLogs",
      fields: `
        ${field("Tarih", "date", "date", true)}
        ${field("Egzersiz", "exerciseName", "text", true)}
        ${field("Set", "sets", "number")}
        ${field("Tekrar", "reps", "number")}
        ${field("Ağırlık (kg)", "weightKg", "number", false, "0.1")}
        ${field("Süre (dk)", "durationMinutes", "number")}
        ${field("RPE (1–10)", "rpe", "number", true, "1", "1", "10")}
        ${selectField("Tamamlandı", "completed", [["true","Evet"],["false","Hayır"]])}
        ${textareaField("Antrenör notu", "coachNote")}`
    },
    summary: {
      title: "Veli özeti taslağı",
      collection: "parentSummaries",
      fields: `
        ${selectField("Özet tipi", "summaryType", [["weekly","Haftalık"],["monthly","Aylık"],["preRace","Yarış öncesi"],["postRace","Yarış sonrası"],["general","Genel"]])}
        ${field("Konu", "subject", "text", true, "", "", "", `${athlete.firstName} ${athlete.lastName} — Gelişim özeti`)}
        ${textareaField("Mesaj", "body", true, `${athlete.firstName} için haftalık gelişim değerlendirmesi:`)}`
    }
  };
  const config = forms[type];
  const m = modal(`<div class="modal-head"><div><p class="eyebrow">${athlete.firstName} ${athlete.lastName}</p><h2>${config.title}</h2></div><button class="close-button" data-close>×</button></div>
    <form id="recordForm"><div class="form-grid">${config.fields}</div><div class="modal-actions"><button type="button" class="secondary-button" data-close>Vazgeç</button><button class="primary-button">Kaydet</button></div></form>`);
  const dateInput = m.querySelector('[name="date"]');
  if (dateInput) dateInput.value = new Date().toISOString().slice(0, 10);
  m.querySelector("form").addEventListener("submit", async event => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.target));
    try {
      let record;
      if (appConfig.demoMode) {
        record = { id: crypto.randomUUID(), athleteId: athlete.id, coachId: "u1", ...values };
      } else {
        const creators = {
          measurement: repositoryApi.createMeasurement,
          attendance: repositoryApi.createAttendance,
          workout: repositoryApi.createWorkoutLog,
          summary: repositoryApi.createParentSummary
        };
        record = await creators[type](currentProfile, athlete.id, values);
      }
      data[config.collection].unshift(record);
      if (appConfig.demoMode) saveData(data);
      m.remove();
      render();
      showToast("Kayıt başarıyla eklendi");
    } catch (error) {
      captureError(error, "record-create");
      showToast("Kayıt eklenemedi. Yetkinizi ve bağlantınızı kontrol edin.");
    }
  });
}

function field(label, name, type = "text", required = false, step = "", min = "", max = "", value = "") {
  return `<div class="field"><label>${label}</label><input name="${name}" type="${type}" ${required ? "required" : ""} ${step ? `step="${step}"` : ""} ${min ? `min="${min}"` : ""} ${max ? `max="${max}"` : ""} ${value ? `value="${value}"` : ""} /></div>`;
}

function selectField(label, name, options) {
  return `<div class="field"><label>${label}</label><select name="${name}">${options.map(option => {
    const [value, text] = Array.isArray(option) ? option : [option, option];
    return `<option value="${value}">${text}</option>`;
  }).join("")}</select></div>`;
}

function textareaField(label, name, required = false, value = "") {
  return `<div class="field full"><label>${label}</label><textarea name="${name}" rows="4" ${required ? "required" : ""}>${value}</textarea></div>`;
}

function wait(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

async function loadCurrentWorkspace() {
  if (!currentProfile) return;
  workspaceLoadError = null;
  sessionStatus = "loading";
  render();

  const retryDelays = [0, 1500, 3500];
  for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
    if (retryDelays[attempt]) await wait(retryDelays[attempt]);
    try {
      const workspace = await repositoryApi.loadWorkspace(currentProfile);
      data = {
        ...workspace,
        currentRole: currentProfile.role,
        loggedIn: true
      };
      entitlements = resolveEntitlements(workspace.subscription);
      sessionStatus = "ready";
      render();
      return;
    } catch (error) {
      workspaceLoadError = {
        code: error?.code || "",
        message: error?.message || ""
      };
      captureError(error, `workspace-load-attempt-${attempt + 1}`);
    }
  }

  sessionStatus = "workspace-error";
  render();
}

async function bootstrap() {
  if (appConfig.demoMode) {
    render();
    return;
  }

  if (!appConfig.firebaseConfigured) {
    app.innerHTML = `<main class="state-page"><div class="state-card">
      <div class="state-icon">⚙</div>
      <h1>Firebase ayarları eksik</h1>
      <p>Uygulamanın üretim yapılandırması tamamlanmadı. Sistem yöneticisiyle iletişime geçin.</p>
    </div></main>`;
    return;
  }

  loadingPage();
  try {
    [authApi, repositoryApi] = await Promise.all([
      import("./auth-service.js"),
      import("./firestore-repository.js")
    ]);
    await authApi.observeSession(async ({ user, profile }) => {
      if (!user) {
        currentAuthUser = null;
        currentProfile = null;
        sessionStatus = "signed-out";
        data = { ...loadData(), loggedIn: false };
        render();
        return;
      }

      currentAuthUser = user;
      currentProfile = profile;
      if (!user.emailVerified) {
        sessionStatus = "email-unverified";
        render();
        return;
      }
      if (profile.status !== "active" || profile.role === "pending") {
        sessionStatus = profile.status === "pending" || profile.role === "pending" ? "pending" : "denied";
        render();
        return;
      }

      enableMonitoring();
      await loadCurrentWorkspace();
    });
  } catch (error) {
    captureError(error, "bootstrap");
    sessionStatus = "signed-out";
    render();
    showToast("Firebase bağlantısı kurulamadı.");
  }
}

bootstrap();
